import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../context/SessionContext';

type ProjectRevision = {
  revisionNumber: number;
  snapshot: Record<string, unknown>;
};

const DEFAULT_SCENE = {
  hierarchy: ['Root', 'AssetInstance', 'KeyLight', 'FillLight', 'Camera'],
  materials: {
    AssetInstance: { baseColor: '#c9ced6', metalness: 0.7, roughness: 0.35 },
  },
  lights: {
    KeyLight: { intensity: 1.2, color: '#ffffff' },
    FillLight: { intensity: 0.4, color: '#dfe7ff' },
  },
  cameras: {
    Camera: { mode: 'perspective', focalLength: 50, exposure: 1 },
  },
  environment: { rotation: 0, background: '#eef1f6' },
};

export function StudioPage() {
  const { projectId } = useParams();
  const { authenticated, startKeyra } = useSession();
  const [selected, setSelected] = useState('AssetInstance');
  const [revision, setRevision] = useState(1);
  const [scene, setScene] = useState(DEFAULT_SCENE);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [projectDbId, setProjectDbId] = useState<string | null>(projectId ?? null);

  const material = scene.materials.AssetInstance;

  useEffect(() => {
    if (!authenticated) return;
    const t = setTimeout(() => {
      void save(true).catch(() => undefined);
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, authenticated]);

  async function ensureProject(): Promise<string> {
    if (projectDbId) return projectDbId;
    const created = await api.createProject({ title: 'Untitled studio project' });
    setProjectDbId(created.id);
    return created.id;
  }

  async function save(autosave = false) {
    if (!authenticated) return;
    try {
      const id = await ensureProject();
      const result = await api.saveProjectRevision(id, {
        expectedRevision: revision,
        snapshot: scene,
      });
      setRevision(result.revisionNumber);
      if (!autosave) setMessage(`Saved revision ${result.revisionNumber}`);
      setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      setError(msg.includes('REVISION_CONFLICT') || msg.includes('conflict')
        ? 'REVISION_CONFLICT — stale client cannot overwrite newer revision'
        : msg);
    }
  }

  async function onMaterialSubmit(e: FormEvent) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    setScene((s) => ({
      ...s,
      materials: {
        ...s.materials,
        AssetInstance: {
          baseColor: String(fd.get('baseColor') ?? '#c9ced6'),
          metalness: Number(fd.get('metalness') ?? 0.7),
          roughness: Number(fd.get('roughness') ?? 0.35),
        },
      },
    }));
  }

  const outliner = useMemo(() => scene.hierarchy, [scene.hierarchy]);

  if (!authenticated) {
    return (
      <main className="page">
        <h1 style={{ fontFamily: 'var(--font-display)' }}>Studio</h1>
        <p className="muted">Studio and projects require an account.</p>
        <button type="button" className="btn primary" onClick={() => void startKeyra('/studio')}>
          Continue with Keyra
        </button>
      </main>
    );
  }

  return (
    <main className="page">
      <h1 style={{ fontFamily: 'var(--font-display)' }}>Studio</h1>
      <p className="muted">
        Multi-object scenes, materials, lights, cameras. Debounced autosave + explicit save. Stale
        revisions are rejected.
      </p>
      <div className="create-layout" style={{ marginTop: '1rem' }}>
        <div className="panel">
          <h3>Outliner</h3>
          <ul>
            {outliner.map((n) => (
              <li key={n}>
                <button type="button" className="btn ghost" onClick={() => setSelected(n)}>
                  {n}
                </button>
              </li>
            ))}
          </ul>
          <p className="muted mono">revision {revision}</p>
        </div>
        <div className="panel">
          <h3>{selected}</h3>
          {selected === 'AssetInstance' && (
            <form onSubmit={onMaterialSubmit}>
              <label className="field">
                Base color
                <input name="baseColor" defaultValue={material.baseColor} />
              </label>
              <label className="field" style={{ display: 'block', marginTop: '0.5rem' }}>
                Metalness
                <input name="metalness" type="number" min={0} max={1} step={0.05} defaultValue={material.metalness} />
              </label>
              <label className="field" style={{ display: 'block', marginTop: '0.5rem' }}>
                Roughness
                <input name="roughness" type="number" min={0} max={1} step={0.05} defaultValue={material.roughness} />
              </label>
              <button className="btn primary" type="submit" style={{ marginTop: '0.75rem' }}>
                Apply material override
              </button>
            </form>
          )}
          {selected === 'Camera' && (
            <label className="field">
              Exposure
              <input
                type="range"
                min={0.2}
                max={2}
                step={0.05}
                value={Number(scene.cameras.Camera.exposure)}
                onChange={(e) =>
                  setScene((s) => ({
                    ...s,
                    cameras: {
                      ...s.cameras,
                      Camera: { ...s.cameras.Camera, exposure: Number(e.target.value) },
                    },
                  }))
                }
              />
            </label>
          )}
          {(selected === 'KeyLight' || selected === 'FillLight') && (
            <label className="field">
              Intensity
              <input
                type="range"
                min={0}
                max={3}
                step={0.05}
                value={Number(scene.lights[selected as 'KeyLight' | 'FillLight'].intensity)}
                onChange={(e) =>
                  setScene((s) => ({
                    ...s,
                    lights: {
                      ...s.lights,
                      [selected]: {
                        ...s.lights[selected as 'KeyLight' | 'FillLight'],
                        intensity: Number(e.target.value),
                      },
                    },
                  }))
                }
              />
            </label>
          )}
          <div className="row" style={{ marginTop: '1rem' }}>
            <button type="button" className="btn primary" onClick={() => void save(false)}>
              Save
            </button>
            <Link className="btn" to="/library">
              Library
            </Link>
          </div>
          {message && <p className="muted">{message}</p>}
          {error && <p className="err">{error}</p>}
        </div>
      </div>
    </main>
  );
}
